import type {
	DefaultPrivacyLevel,
	PropagatorType,
	Site
} from '@datadog/browser-rum-slim';

import { defineNuxtPlugin } from '#app';
import { deepToRaw } from '~/utils/deepToRaw';
import { isBotUserAgent } from '~/utils/isBotUserAgent';
import { SHORTS_FIRST_FRAME_EVENT } from '~/utils/shortsEvents';
import { registerDatadogSdk } from '~~/composables/useDatadog';

const SHORTS_DEFER_CAP_MS = 5000;

// Constants for filtering
const BLOCKED_DOMAINS = [
	'sa.stape.co',
	'monitor.clickcease.com',
	'google-analytics.com',
	'trc-events.taboola.com',
	'content.hotjar.io'
];

// Filter helper functions
function shouldFilterObjectNotFound(message: string): boolean {
	return message.includes('Object Not Found Matching');
}

function shouldFilterScriptError(message: string): boolean {
	return message.includes('Uncaught "Script error."');
}

// Benign browser warning: ResizeObserver callbacks deferred to the next
// frame. Fired via window.onerror, so it pollutes @error.source:source.
// Covers both variants: "loop completed with undelivered notifications"
// and the older "loop limit exceeded".
function shouldFilterResizeObserverLoop(message: string, stack = ''): boolean {
	const combined = (message + ' ' + stack).toLowerCase();
	return (
		combined.includes('resizeobserver') &&
		(combined.includes('loop') ||
			combined.includes('undelivered') ||
			combined.includes('exceeded'))
	);
}

// Browser intervention report (IgnoredEventCancel): third-party code
// (Google Maps touch handling) tries to cancel a touchmove/touchend with
// cancelable=false during scroll. Spec-mandated refusal, no user impact.
function shouldFilterIgnoredEventCancel(message: string): boolean {
	return message.includes('Ignored attempt to cancel a touch');
}

// Shaka Error 7003 (OBJECT_DESTROYED): the player was destroyed while an
// operation was in flight — deliberate teardown when the user scrolls past
// a video. App code catches its own load() promises; this rejection leaks
// from Shaka's internal chain (known library issue), so drop it here.
function shouldFilterShakaObjectDestroyed(message: string): boolean {
	return message.includes('Shaka Error 7003');
}

// Network abort: fetch AbortError from user navigation or component unmount.
// Not an error — normal lifecycle when AbortController signal fires.
// "Failed to fetch" alone is too broad (also genuine API/DNS/connectivity failures).
function shouldFilterNetworkAbort(message: string): boolean {
	return message.includes('AbortError: The operation was aborted');
}

// Native app bridge unavailable: Code written for native iOS/Android apps
// accessing window.webkit.messageHandlers that doesn't exist in web context.
// Occurs when web code runs inside a WebView without proper capability checks.
function shouldFilterNativeAppBridgeUnavailable(message: string): boolean {
	return (
		message.includes('window.webkit') ||
		message.includes('webkit.messageHandlers') ||
		(message.includes('undefined is not an object') &&
			message.includes('messageHandlers'))
	);
}

// WebView destroyed: Native app WebView was torn down while a postMessage
// was in flight. Common in mobile app lifecycle transitions. No user impact,
// only indicates app/browser context change.
function shouldFilterWebViewDestroyed(message: string): boolean {
	// Android bridge teardown — this string is bridge-specific, keep as-is.
	if (message.includes('Java object is gone')) return true;

	// Bare 'postMessage'/'WebView' matches genuine web bugs too (OAuth popups,
	// payment iframes, cross-origin messaging). Only drop them when the message
	// also signals a destroyed/detached context, i.e. real WebView teardown.
	const mentionsWebViewBridge =
		message.includes('postMessage') || message.includes('WebView');
	const mentionsTeardown =
		message.includes('destroyed') ||
		message.includes('detached') ||
		message.includes('no longer') ||
		message.includes('is gone') ||
		message.includes('unloaded');
	return mentionsWebViewBridge && mentionsTeardown;
}

// Video playback errors (AbortError, NotAllowedError): Mobile browsers
// aggressively restrict autoplay: play() fails after mute() or before
// user interaction. App already handles this gracefully. Common on iOS
// and Android when user hasn't touched video controls yet.
// AbortError alone is too broad (also ResizeObserver, IndexedDB, other APIs);
// only filter when video-specific terms confirm the context.
function shouldFilterVideoPlaybackError(message: string): boolean {
	return (
		message.includes('NotAllowedError') ||
		(message.includes('AbortError') &&
			(message.includes('play') || message.includes('video'))) ||
		(message.includes('play()') &&
			(message.includes('failed') ||
				message.includes('permission') ||
				message.includes('denied'))) ||
		message.includes('safePlay')
	);
}

function isNetworkErrorMessage(message: string, stack: string): boolean {
	const messageLC = message.toLowerCase();
	const stackLC = stack.toLowerCase();
	return (
		messageLC.includes('fetch') ||
		stackLC.includes('fetch') ||
		messageLC.includes('xhr') ||
		stackLC.includes('xhr') ||
		messageLC.includes('xmlhttprequest') ||
		stackLC.includes('xmlhttprequest')
	);
}

function shouldFilterBlockedDomainError(
	message: string,
	stack: string
): boolean {
	const isNetworkError = isNetworkErrorMessage(message, stack);
	if (!isNetworkError) return false;

	return BLOCKED_DOMAINS.some(
		domain => message.includes(domain) || stack.includes(domain)
	);
}

export default defineNuxtPlugin({
	name: 'datadog-plugin',
	setup() {
		const initializeDatadog = async () => {
			// Skip Datadog initialization during tests
			if (import.meta.env.VITEST || import.meta.env.MODE === 'test') {
				return;
			}

			// Known crawlers: keep sessions/views/resources for bot-traffic
			// visibility, but drop their error/log noise in beforeSend below.
			const isBot = isBotUserAgent();

			try {
				// rum-slim: same RUM API without the bundled Session Replay recorder
				// (replay is unused here — startSessionReplayRecording is never called).
				const { datadogRum } = await import('@datadog/browser-rum-slim');
				const { datadogLogs } = await import('@datadog/browser-logs');
				const config = useRuntimeConfig();
				const datadogConfig = config.public.datadog;
				const appUrl = config.public.APP_URL;
				if (!datadogConfig.applicationId || !datadogConfig.clientToken) {
					console.warn(
						'Datadog: Application ID ou Client Token não configurados'
					);
					return;
				}
				// Extract hostname from APP_URL for tracing configuration
				let firstPartyHosts: Array<{
					match: string | RegExp;
					propagatorTypes: PropagatorType[];
				}> = [];
				if (appUrl && typeof appUrl === 'string') {
					try {
						const url = new URL(appUrl);
						const hostname = url.hostname;
						// Configure first-party hosts for RUM-APM connection
						// Only trace requests to the current domain to avoid CORS issues with external APIs
						firstPartyHosts = [
							{
								match: hostname,
								propagatorTypes: [
									'datadog' as PropagatorType,
									'tracecontext' as PropagatorType
								]
							},
							// Only include API routes from the current domain
							{
								match: new RegExp(
									`^https?://${hostname.replace(/\./g, '\\.')}/api/.*`
								),
								propagatorTypes: [
									'datadog' as PropagatorType,
									'tracecontext' as PropagatorType
								]
							}
						];
					} catch (error) {
						console.warn(
							'Datadog: Invalid APP_URL format, using fallback configuration',
							error
						);
						// Fallback: only trace relative API routes (same domain)
						firstPartyHosts = [
							{
								match: /^\/api\/.*/,
								propagatorTypes: [
									'datadog' as PropagatorType,
									'tracecontext' as PropagatorType
								]
							}
						];
					}
				} else {
					console.warn(
						'Datadog: APP_URL not configured, using fallback configuration'
					);
					// Fallback: only trace relative API routes (same domain)
					firstPartyHosts = [
						{
							match: /^\/api\/.*/,
							propagatorTypes: [
								'datadog' as PropagatorType,
								'tracecontext' as PropagatorType
							]
						}
					];
				}
				// Configure user session identification
				const configureUserSession = async (datadogRum: any) => {
					// Check if user is authenticated using Nuxt Auth Utils
					const { loggedIn, user } = useUserSession();
					if (loggedIn.value && user.value) {
						// Set user information for session tracking using the new API
						const userData: any = user.value;
						// Use individual property setters instead of deprecated setUser
						datadogRum.setUserProperty('id', userData.id || userData.email);
						if (userData.name)
							datadogRum.setUserProperty('name', userData.name);
						if (userData.email)
							datadogRum.setUserProperty('email', userData.email);
						if (userData.role)
							datadogRum.setUserProperty('role', userData.role);
						if (userData.plan)
							datadogRum.setUserProperty('plan', userData.plan);
						// Add user context for better filtering
						datadogRum.addAction('user_identified', {
							user_type: 'authenticated',
							user_id: userData.id || userData.email
						});
					} else {
						// For anonymous users, you can still track some session info
						datadogRum.setUserProperty('id', `anonymous_${Date.now()}`);
						datadogRum.setUserProperty('session_type', 'anonymous');
					}
					// Watch for user session changes
					watch([loggedIn, user], ([newLoggedIn, newUser]) => {
						if (newLoggedIn && newUser) {
							const newUserData: any = newUser;
							// Use individual property setters instead of deprecated setUser
							datadogRum.setUserProperty(
								'id',
								newUserData.id || newUserData.email
							);
							if (newUserData.name)
								datadogRum.setUserProperty('name', newUserData.name);
							if (newUserData.email)
								datadogRum.setUserProperty('email', newUserData.email);
							if (newUserData.role)
								datadogRum.setUserProperty('role', newUserData.role);
							if (newUserData.plan)
								datadogRum.setUserProperty('plan', newUserData.plan);
							datadogRum.addAction('user_logged_in', {
								user_id: newUserData.id || newUserData.email
							});
						} else {
							// User logged out
							datadogRum.clearUser();
							datadogRum.addAction('user_logged_out');
						}
					});
				};
				// Wrap datadogLogs.logger methods to automatically deserialize Vue
				// Proxy objects — prevents DataCloneError when the SDK structured-clones
				// contexts, without requiring manual toRaw() calls at every log site.
				const wrapLoggerWithDeepToRaw = () => {
					if (!window.DD_LOGS?.logger) return;
					const originalLogger = window.DD_LOGS.logger;
					const loggerMethods = ['debug', 'info', 'warn', 'error'] as const;

					loggerMethods.forEach(method => {
						const original = originalLogger[method];
						originalLogger[method] = (
							message: string,
							context?: Record<string, unknown>
						) => {
							return original.call(
								originalLogger,
								message,
								context ? (deepToRaw(context) as object) : context
							);
						};
					});
				};
				// Initialize RUM (Real User Monitoring)
				datadogRum.init({
					applicationId: datadogConfig.applicationId,
					clientToken: datadogConfig.clientToken,
					...(datadogConfig.site && { site: datadogConfig.site as Site }),
					service: datadogConfig.service,
					env: datadogConfig.env || 'development',
					version: datadogConfig.version || '1.0.0',
					sessionSampleRate: datadogConfig.sessionSampleRate,
					sessionReplaySampleRate: datadogConfig.sessionReplaySampleRate,
					...(datadogConfig.defaultPrivacyLevel && {
						defaultPrivacyLevel:
							datadogConfig.defaultPrivacyLevel as DefaultPrivacyLevel
					}),
					trackUserInteractions: datadogConfig.trackUserInteractions,
					trackResources: datadogConfig.trackResources,
					trackLongTasks: datadogConfig.trackLongTasks,
					// Enable experimental features if configured
					...(datadogConfig.enableExperimentalFeatures.length > 0 && {
						enableExperimentalFeatures: datadogConfig.enableExperimentalFeatures
					}),
					// Performance-specific configurations
					trackingConsent: 'granted',
					// Enable distributed tracing for backend connections (RUM-APM connection)
					allowedTracingUrls: firstPartyHosts,
					// Enable Web Vitals collection (Core Web Vitals are automatically collected when trackResources and trackLongTasks are true)
					trackViewsManually: false, // Automatically track page views
					// Customization for the real estate portal
					beforeSend: (event, context) => {
						// Bots: keep requests (resources/views), drop error noise
						if (
							isBot &&
							(event.type === 'error' || event.type === 'long_task')
						) {
							return false;
						}
						// Filter errors containing specific messages
						if (event.type === 'error' && event.error?.message) {
							const errorMessage = event.error.message;
							const errorStack = event.error.stack || '';

							if (shouldFilterObjectNotFound(errorMessage)) {
								return false;
							}

							if (shouldFilterScriptError(errorMessage)) {
								return false;
							}

							if (shouldFilterResizeObserverLoop(errorMessage, errorStack)) {
								return false;
							}

							if (shouldFilterIgnoredEventCancel(errorMessage)) {
								return false;
							}

							if (shouldFilterShakaObjectDestroyed(errorMessage)) {
								return false;
							}

							if (shouldFilterNetworkAbort(errorMessage)) {
								return false;
							}

							if (shouldFilterNativeAppBridgeUnavailable(errorMessage)) {
								return false;
							}

							if (shouldFilterWebViewDestroyed(errorMessage)) {
								return false;
							}

							if (shouldFilterVideoPlaybackError(errorMessage)) {
								return false;
							}

							if (shouldFilterBlockedDomainError(errorMessage, errorStack)) {
								return false;
							}
						}
						// Filter sensitive data before sending
						if (
							event.type === 'resource' &&
							event.resource.url.includes('sensitive')
						) {
							return false;
						}
						// Enrich events with additional context
						if (event.type === 'view') {
							// Add page-specific context for property/real estate events
							const url = window.location.pathname;
							if (url.includes('/imovel/')) {
								event.context = {
									...event.context,
									page_type: 'property_detail',
									section: 'properties'
								};
							} else if (url.includes('/venda/imoveis/')) {
								event.context = {
									...event.context,
									page_type: 'property_listing',
									section: 'properties'
								};
							} else if (url.includes('/bairros/')) {
								event.context = {
									...event.context,
									page_type: 'neighborhood',
									section: 'regions'
								};
							} else if (url.includes('/condominio/')) {
								event.context = {
									...event.context,
									page_type: 'condominium',
									section: 'condominiums'
								};
							}
						}
						return true;
					}
				});
				// Configure user session identification
				await configureUserSession(datadogRum);
				// Initialize Logs
				datadogLogs.init({
					clientToken: datadogConfig.clientToken,
					...(datadogConfig.site && { site: datadogConfig.site as Site }),
					service: datadogConfig.service,
					env: datadogConfig.env || 'development',
					version: datadogConfig.version || '1.0.0',
					sessionSampleRate: 100,
					silentMultipleInit: true,
					// Log-specific configurations
					beforeSend: (log, context) => {
						// Bots: logs carry no value, only ingestion cost
						if (isBot) {
							return false;
						}
						const logMessage = log.message || '';
						const logError = (log as any).error || {};
						const errorStack = logError.stack || '';

						// Filter logs containing specific error messages
						if (shouldFilterObjectNotFound(logMessage)) {
							return false;
						}

						if (shouldFilterScriptError(logMessage)) {
							return false;
						}

						// ResizeObserver: benign browser noise, already filtered in RUM
						if (shouldFilterResizeObserverLoop(logMessage, errorStack)) {
							return false;
						}

						// Native app bridge (window.webkit.messageHandlers) / WebView
						// teardown: injected native code failing capability checks. No user
						// impact — already dropped in RUM, mirror it here so it stops
						// polluting the logs pipeline too.
						if (shouldFilterNativeAppBridgeUnavailable(logMessage)) {
							return false;
						}

						if (shouldFilterWebViewDestroyed(logMessage)) {
							return false;
						}

						if (shouldFilterBlockedDomainError(logMessage, errorStack)) {
							return false;
						}

						// Filter sensitive data before sending
						if (
							log.message?.includes('sensitive') ||
							(log as any).http?.url?.includes('sensitive')
						) {
							return false;
						}
						// Remove sensitive information from logs
						if (
							log.message?.includes('password') ||
							log.message?.includes('token')
						) {
							log.message = log.message.replace(
								/(password=)[^&\s]+/gi,
								'$1***'
							);
							log.message = log.message.replace(/(token=)[^&\s]+/gi, '$1***');
						}
						return true;
					}
				});
				wrapLoggerWithDeepToRaw();
				// Session Replay disabled to improve performance
				// Registro dos SDKs nos proxies do useDatadog so DEPOIS dos dois
				// init(): chamadas na janela import→init batiam no SDK cru.
				registerDatadogSdk({ rum: datadogRum, logs: datadogLogs });
				// Use Hotjar for session recording instead, or enable this and disable Hotjar
				// datadogRum.startSessionReplayRecording();
				// Successful initialization log
				// datadogLogs.logger.info('Datadog inicializado com sucesso', {
				// 	service: datadogConfig.service,
				// 	environment: datadogConfig.env
				// });
				// console.log('Datadog inicializado com sucesso');
			} catch (error) {
				console.error('Erro ao inicializar Datadog:', error);
			}
		};
		if (typeof window !== 'undefined') {
			// Defer Datadog initialization to improve INP and LCP
			// This ensures the main thread is free for user interactions first
			const scheduleIdle = () => {
				if ('requestIdleCallback' in window) {
					requestIdleCallback(() => initializeDatadog(), { timeout: 5000 });
				} else {
					// Fallback for browsers without requestIdleCallback
					setTimeout(initializeDatadog, 2000);
				}
			};

			// Shorts entries defer harder: on slow networks the RUM chunks
			// (~150KB) compete with the video segments for the same pipe, so
			// wait for the entry video's first frame (or interaction, or the
			// cap) before even scheduling the idle callback. Frozen at boot on
			// purpose — SPA navigations into /shorts already paid this cost.
			if (useRoute().path.startsWith('/shorts')) {
				let settled = false;
				const cleanups: Array<() => void> = [];
				const fire = () => {
					if (settled) return;
					settled = true;
					for (const undo of cleanups) undo();
					scheduleIdle();
				};
				const timer = setTimeout(fire, SHORTS_DEFER_CAP_MS);
				cleanups.push(() => clearTimeout(timer));
				window.addEventListener(SHORTS_FIRST_FRAME_EVENT, fire, {
					once: true
				});
				cleanups.push(() =>
					window.removeEventListener(SHORTS_FIRST_FRAME_EVENT, fire)
				);
				window.addEventListener('pointerdown', fire, { once: true });
				cleanups.push(() => window.removeEventListener('pointerdown', fire));
			} else {
				scheduleIdle();
			}
		}
	}
});
